import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import LoginPage      from './pages/LoginPage';
import DashboardPage  from './pages/DashboardPage';
import BookingsPage   from './pages/BookingsPage';
import BookingDetailsPage from './pages/BookingDetailsPage';
import NewBookingPage from './pages/NewBookingPage';
import CalendarPage   from './pages/CalendarPage';
import ApprovalsPage  from './pages/ApprovalsPage';
import EventAreasPage from './pages/EventAreasPage';
import AssetsPage     from './pages/AssetsPage';
import UsersPage      from './pages/UsersPage';
import EmailNotificationPage from './pages/EmailNotificationPage';
import SettingsPage   from './pages/SettingsPage';
import HolidaySettingsPage from './pages/HolidaySettingsPage';
import { filterByLastNDays } from './utils/dateRange';
import './styles/global.css';

// ── Protected Route ──────────────────────────────────────────
function Protected({ children, adminOnly = false, managementOnly = false }) {
  const { user, isAdmin, isManagement, isViewer } = useAuth();
  const location = useLocation();
  if (!user) return <Navigate to="/login" replace />;
  if (isViewer && location.pathname !== '/calendar') return <Navigate to="/calendar" replace />;
  if (adminOnly      && !isAdmin)      return <Navigate to="/dashboard" replace />;
  if (managementOnly && !isManagement) return <Navigate to="/dashboard" replace />;
  return children;
}

// ── App shell with pending count ─────────────────────────────
function AppShell() {
  const { user, isManagement } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (!isManagement || !user) return;
    import('./services/api').then(({ bookingApi }) => {
      bookingApi.getPending(user.campusID)
        .then(r => setPendingCount(filterByLastNDays(r.data.data || [], 6, 'submittedAt').length))
        .catch(() => setPendingCount(3)); // fallback demo
    });
  }, [isManagement, user]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        <Route path="/dashboard" element={
          <Protected><Layout pendingCount={pendingCount}><DashboardPage /></Layout></Protected>
        }/>
        <Route path="/bookings" element={
          <Protected><Layout pendingCount={pendingCount}><BookingsPage /></Layout></Protected>
        }/>
        <Route path="/bookings/:id" element={
          <Protected><Layout pendingCount={pendingCount}><BookingDetailsPage /></Layout></Protected>
        }/>
        <Route path="/new-booking" element={
          <Protected><Layout pendingCount={pendingCount}><NewBookingPage /></Layout></Protected>
        }/>
        <Route path="/calendar" element={
          <Protected><Layout pendingCount={pendingCount}><CalendarPage /></Layout></Protected>
        }/>
        <Route path="/approvals" element={
          <Protected managementOnly><Layout pendingCount={pendingCount}><ApprovalsPage /></Layout></Protected>
        }/>
        <Route path="/areas" element={
          <Protected managementOnly><Layout pendingCount={pendingCount}><EventAreasPage /></Layout></Protected>
        }/>
        <Route path="/assets" element={
          <Protected managementOnly><Layout pendingCount={pendingCount}><AssetsPage /></Layout></Protected>
        }/>
        <Route path="/users" element={
          <Protected adminOnly><Layout pendingCount={pendingCount}><UsersPage /></Layout></Protected>
        }/>
        <Route path="/email-config" element={
          <Protected adminOnly><Layout pendingCount={pendingCount}><EmailNotificationPage /></Layout></Protected>
        }/>
        <Route path="/settings" element={
          <Protected adminOnly><Layout pendingCount={pendingCount}><SettingsPage /></Layout></Protected>
        }/>
        <Route path="/holidays" element={
          <Protected adminOnly><Layout pendingCount={pendingCount}><HolidaySettingsPage /></Layout></Protected>
        }/>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}
